import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import moment from "moment";
import { FC, useMemo } from "react";
import { RentReportingData } from "../types";
import { Label } from "views/rent-reporting/components/SelectPayment";
import { SelectAndReport } from "./SelectAndReport";
import { useParams } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { RvStatus } from "views/rent-reporting/constants";
import { RvStatusBadge } from "views/rent-reporting/components/RvStatusBadge";

type Props = {
  reportData?: RentReportingData[];
};

export const RentReportingTable: FC<Props> = ({ reportData }) => {
  const { renterId } = useParams();
  const { type } = useAuthContext();

  const earliestPendingVerification = useMemo(() => {
    return reportData?.reduce(
      (acc: RentReportingData | null, item: RentReportingData) =>
        item.status.status === RvStatus.PendingVerification &&
        (!acc || new Date(item.for_month) < new Date(acc.for_month))
          ? item
          : acc,
      null
    );
  }, [reportData]);

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "for_month",
        header: "Month",
        cell: (p) => {
          return (
            <span className="text-sm text-content-1000 font-medium">
              {p.getValue() ? moment(p.getValue()).format("MMM YYYY") : ""}
            </span>
          );
        }
      },
      {
        id: "rent_amount fixed_width",
        accessorKey: "rent_amount",
        header: "Rent",
        size: 90,
        cell: (p) => {
          const rentAmount = p.getValue()
            ? `$${p.getValue().cents / 100}`
            : "-";
          return (
            <p className="text-sm text-content-1000 font-medium">
              {rentAmount}
            </p>
          );
        }
      }
    ];
    if (!type.isSDK)
      cols.push({
        id: "status fixed_width",
        accessorKey: "",
        header: "Payment status",
        size: 240,
        cell: (p) => {
          const isEarliestPendingVerification =
            p.row.original.id === earliestPendingVerification?.id;
          const delinquencyOptions =
            p.row.original?.available_delinquency_options;
          const options = delinquencyOptions?.length
            ? delinquencyOptions.map((option) => {
                return {
                  value: option?.months_delinquency,
                  label: (
                    <Label
                      title={option.name}
                      icon={
                        <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                          <p className="text-danger-500 text-sm font-bold">
                            {option?.months_delinquency}
                          </p>
                        </div>
                      }
                    />
                  )
                };
              })
            : [];
          return isEarliestPendingVerification ? (
            <SelectAndReport
              verificationId={p.row.original.id}
              subscriberId={renterId}
              options={options}
            />
          ) : p.row.original?.status?.status ===
            RvStatus.PendingVerification ? (
            <div />
          ) : (
            <RvStatusBadge status={p.row.original?.status} />
          );
        }
      });

    return cols;
  }, []);
  const table = useReactTable({
    data: reportData || [],
    columns: columns as ColumnDef<any, any>[],
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <Table
      table={table}
      emptyStateTitle="No rent for reporting yet"
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      mobileRow={(row) => {
        const isEarliestPendingVerification =
          row.original.id === earliestPendingVerification?.id;
        const delinquencyOptions = row.original?.available_delinquency_options;
        const options = delinquencyOptions?.length
          ? delinquencyOptions.map((option) => {
              return {
                value: option?.months_delinquency,
                label: (
                  <Label
                    title={option.name}
                    icon={
                      <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                        <p className="text-danger-500 text-sm font-bold">
                          {option?.months_delinquency}
                        </p>
                      </div>
                    }
                  />
                )
              };
            })
          : [];
        return (
          <div className="space-y-2">
            <p className="text-sm text-content-700">
              <span>
                {row.original.for_month
                  ? moment(row.original.for_month).format("MMM YYYY")
                  : ""}
              </span>
              <span> - </span>
              <span>
                {row.original.rent_amount
                  ? `$${row.original.rent_amount.cents / 100}`
                  : "-"}
              </span>
            </p>
            {!type.isSDK && (
              <div className="flex gap-2">
                {isEarliestPendingVerification ? (
                  <SelectAndReport
                    verificationId={row.original.id}
                    subscriberId={renterId}
                    options={options}
                  />
                ) : row.original?.status?.status ===
                  RvStatus.PendingVerification ? (
                  <div />
                ) : (
                  <RvStatusBadge status={row.original?.status} />
                )}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
